import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXS, BrandXXL } from '@sorare/core/src/atoms/typography';
import { Countdown } from '@sorare/core/src/components/Countdown';
import { AbsoluteNav } from '@sorare/core/src/components/navigation/AppNavigation/index.tsx';
import { ANY_SPORT_SERIES_ONBOARDING_SELECT_COMPETITION } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSetBackground, useSetLogoImage } from '@sorare/core/src/set';

import type { ClaimStarterPack_cardSetRoot } from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  min-height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
  position: relative;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

const Content = styled(Vertical)`
  position: relative;
  z-index: 1;
  flex: 1;
  padding: var(--triple-unit);
  justify-content: space-between;
  align-items: center;
`;

const CountdownLabel = styled(BrandXXL)`
  font-variant-numeric: tabular-nums;
`;

const CountdownCaption = styled(BrandXS)`
  color: var(--c-nd-600);
`;

const CTAWrapper = styled.div`
  width: 100%;
  max-width: 480px;
  padding-bottom: calc(10 * var(--unit));
`;

type Props = { cardSetRoot: ClaimStarterPack_cardSetRoot; refetch: () => void };
export const ClaimStarterPack = ({ cardSetRoot, refetch }: Props) => {
  const getSetBackground = useSetBackground();
  const getSetLogoImage = useSetLogoImage();
  const { generateSportContextPath } = useSportContext();
  const { currentSet, nextSet } = cardSetRoot;

  const showTimer = !currentSet && !!nextSet?.startDate;
  const background = getSetBackground(
    showTimer ? nextSet?.name : currentSet?.name
  );
  const logoImage = getSetLogoImage(
    showTimer ? nextSet?.name : currentSet?.name
  );

  return (
    <Root>
      <Background style={background} />
      <Content gap={1.5} center>
        <img src={logoImage} alt="" height={48} />
        {!!showTimer && (
          <Vertical center gap={1.5}>
            <BrandS>
              <FormattedMessage defaultMessage="begins in" />
            </BrandS>
            <Countdown
              date={nextSet.startDate}
              Label={CountdownLabel}
              Caption={CountdownCaption}
              gap={3}
              onEnded={refetch}
            />
          </Vertical>
        )}
        <CTAWrapper>
          <Button
            size="large"
            color="primary"
            to={generateSportContextPath(
              ANY_SPORT_SERIES_ONBOARDING_SELECT_COMPETITION
            )}
            fullWidth
            disabled={!currentSet}
          >
            <FormattedMessage defaultMessage="Claim my starter pack" />
          </Button>
        </CTAWrapper>
      </Content>
      <AbsoluteNav />
    </Root>
  );
};

ClaimStarterPack.fragments = {
  cardSetRoot: gql`
    fragment ClaimStarterPack_cardSetRoot on CardSetRoot {
      id
      currentSet {
        slug
        name
      }
      nextSet {
        slug
        name
        startDate
      }
    }
  ` as TypedDocumentNode<ClaimStarterPack_cardSetRoot>,
};
